import { Nullable } from 'models/helpers'
import { uniqueId } from 'lodash'
import { useRef } from 'react'

/**
 * Represents a timed event with start, end, and delta times.
 */
interface TimedEvent {
  /** Timestamp in milliseconds when the event started, or `null` if not started. */
  start: Nullable<number>
  /** Timestamp in milliseconds when the event stopped, or `null` if not stopped. */
  end: Nullable<number>
  /** Elapsed time in milliseconds between `start` and `end`, or `null` if not calculated. */
  delta: Nullable<number>
  /** Unique name of the event. */
  name: string
  /**
   * Starts the timer for this event.
   * @returns The current `TimedEvent` instance with `start` time set.
   */
  startTimer: () => TimedEvent
  /**
   * Stops the timer for this event.
   * @returns The updated `TimedEvent` instance with `end` and `delta` set, or `-1` if the event was never started.
   */
  stopTimer: () => TimedEvent | -1
}

/**
 * React hook for creating and managing named timers.
 * 
 * Provides utilities to start, stop, and retrieve timed events.
 * Events are stored in a persistent in-memory registry for the lifetime of the hook.
 *
 * @example
 * ```tsx
 * const { startEventTimer, stopEventTimer, getEvent } = useEventTimer();
 * 
 * const event = startEventTimer("apiCall");
 * // ... perform async work
 * const finishedEvent = stopEventTimer("apiCall");
 * console.log(finishedEvent?.delta); // elapsed time in ms
 * ```
 */
export const useEventTimer = () => {
  const eventLibrary = useRef<Record<string, TimedEvent>>({})

  /**
   * Creates a new event and stores it in the event library.
   * If no name is provided, a unique ID will be generated.
   * 
   * @param eventName - Optional custom name for the event.
   * @returns The created `TimedEvent` instance.
   */
  const createEvent = (eventName?: string) => {
    const name = eventName ?? uniqueId('timed-event')

    const event: TimedEvent = {
      name,
      start: null,
      end: null,
      delta: null,
      startTimer() {
        this.start = new Date().getTime()

        eventLibrary.current[this.name] = this

        return this
      },
      stopTimer() {
        if (!this.start) return -1

        this.end = new Date().getTime()
        this.delta = this.end - this.start

        eventLibrary.current[this.name] = this

        return this
      }
    }

    eventLibrary.current[name] = event
    return event
  }

  /**
   * Retrieves an event by its name.
   * 
   * @param eventName - The name of the event to retrieve.
   * @returns The corresponding `TimedEvent` or `-1` if not found.
   */
  const getEvent = (eventName: string): TimedEvent | -1 => {
    return eventLibrary.current[eventName] ?? -1
  }

  /**
   * Creates and starts a new event timer.
   * 
   * @param eventName - Optional name for the event. If not provided, a unique one will be generated.
   * @returns The started `TimedEvent` instance.
   */
  const startEventTimer = (eventName?: string) => {
    const event = createEvent(eventName).startTimer()

    return event
  }

  /**
   * Stops an existing event timer.
   * 
   * @param eventName - The name of the event to stop.
   * @returns The updated `TimedEvent` with `end` and `delta` set, or `-1` if the event does not exist or was never started.
   */
  const stopEventTimer = (eventName: string) => {
    const event = getEvent(eventName)

    if (event === -1) return event

    return event.stopTimer()
  }

  return {
    startEventTimer,
    stopEventTimer,
    getEvent,
    createEvent,
  }
}
